// Floating window layer — ported from the /dev desktop preview, upgraded to:
//  • pointer events (mouse + touch/stylus) instead of mouse-only drag/resize
//  • real route content via WindowContent (MemoryRouter), not iframes
// Windows live below the top taskbar (top-12 on md+) and above page content.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useWindowManager,
  useWindowed,
  MIN_W,
  MIN_H,
  type WinState,
} from '@/lib/context/WindowManagerContext';
import { WindowContent } from './WindowContent';

// Pinned windows sit above all non-pinned ones (still ordered among themselves).
const PIN_Z_BOOST = 1_000_000;

type DragType = 'move' | 'resize-se' | 'resize-e' | 'resize-s';

interface DragState {
  id: string;
  type: DragType;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  origW: number;
  origH: number;
}

export const WindowLayer = () => {
  const {
    windows,
    activeId,
    closeWindow,
    minimizeWindow,
    toggleMaximize,
    togglePin,
    focusWindow,
    setWindowBounds,
    snapWindow,
  } = useWindowManager();
  const windowed = useWindowed();
  const navigate = useNavigate();
  const [drag, setDrag] = useState<DragState | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  // #185: "maximize" leaves the window system — close the window and go to its
  // real URL as a normal full page.
  const maximizeToPage = useCallback(
    (win: WinState) => {
      closeWindow(win.id);
      navigate(win.route);
    },
    [closeWindow, navigate]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent, win: WinState, type: DragType) => {
      if (win.maximized && type !== 'move') return;
      // Ignore drags started on buttons inside the titlebar.
      if ((e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      focusWindow(win.id);
      // Firefox can throw InvalidPointerId here (e.g. synthesized events) — non-fatal.
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
      setDrag({
        id: win.id,
        type,
        startX: e.clientX,
        startY: e.clientY,
        origX: win.x,
        origY: win.y,
        origW: win.w,
        origH: win.h,
      });
    },
    [focusWindow]
  );

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const layer = layerRef.current?.getBoundingClientRect();
      if (drag.type === 'move') {
        const maxX = (layer?.width ?? window.innerWidth) - 80;
        setWindowBounds(drag.id, {
          x: Math.min(Math.max(drag.origX + dx, -drag.origW + 120), maxX),
          y: Math.max(0, drag.origY + dy),
        });
      } else {
        const w =
          drag.type === 'resize-s' ? drag.origW : Math.max(MIN_W, drag.origW + dx);
        const h =
          drag.type === 'resize-e' ? drag.origH : Math.max(MIN_H, drag.origH + dy);
        setWindowBounds(drag.id, { w, h });
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag, setWindowBounds]);

  // Never render a window layer inside another window.
  if (windowed || windows.length === 0) return null;

  return (
    <div
      ref={layerRef}
      className="fixed inset-x-0 top-14 md:top-12 z-40 pointer-events-none overflow-hidden bottom-[calc(3.75rem+env(safe-area-inset-bottom))] md:bottom-0"
    >
      {windows.map(win =>
        win.minimized ? null : (
          <div
            key={win.id}
            className={`absolute pointer-events-auto flex flex-col rounded-xl overflow-hidden border shadow-2xl bg-white ${
              activeId === win.id ? 'border-[#C77D3F]/50' : 'border-gray-300/80'
            }`}
            style={
              win.maximized
                ? // transform creates a containing block so fixed-position modals
                  // inside the page stay INSIDE the window.
                  { inset: 0, zIndex: win.zIndex + (win.pinned ? PIN_Z_BOOST : 0), transform: 'translateZ(0)' }
                : {
                    left: win.x,
                    top: win.y,
                    width: win.w,
                    height: win.h,
                    zIndex: win.zIndex + (win.pinned ? PIN_Z_BOOST : 0),
                    transform: 'translateZ(0)',
                  }
            }
            onPointerDown={() => focusWindow(win.id)}
          >
            {/* Titlebar — slim OchrePoint chrome (#187): icon + title left,
                tile / pin / − □ × on the right. Drag handle + double-click
                still toggles the in-layer full-size state. */}
            <div
              className={`h-10 md:h-7 flex items-center gap-1.5 pl-2 pr-1 flex-shrink-0 select-none touch-none cursor-grab active:cursor-grabbing border-b ${
                activeId === win.id
                  ? 'bg-[#1e1e2e] border-white/10'
                  : 'bg-[#2a2a3a] border-white/5'
              }`}
              onPointerDown={e => onPointerDown(e, win, 'move')}
              onDoubleClick={() => toggleMaximize(win.id)}
            >
              <span className="material-symbols-outlined text-sm text-[#C77D3F]">
                {win.icon}
              </span>
              <span className="text-xs font-semibold text-gray-200 truncate">{win.title}</span>

              <div className="ml-auto flex items-center flex-shrink-0">
                {/* Tiling (#187) — snap to left/right half; desktop only */}
                <button
                  onClick={() => snapWindow(win.id, 'left')}
                  className="hidden md:flex w-7 h-7 md:h-6 md:w-6 rounded items-center justify-center text-gray-500 hover:text-[#C77D3F] hover:bg-white/10"
                  title="Tile left half"
                >
                  <span className="material-symbols-outlined text-sm">west</span>
                </button>
                <button
                  onClick={() => snapWindow(win.id, 'right')}
                  className="hidden md:flex w-7 h-7 md:h-6 md:w-6 rounded items-center justify-center text-gray-500 hover:text-[#C77D3F] hover:bg-white/10"
                  title="Tile right half"
                >
                  <span className="material-symbols-outlined text-sm">east</span>
                </button>
                {/* Pin — always on top */}
                <button
                  onClick={() => togglePin(win.id)}
                  className={`w-9 h-9 md:h-6 md:w-6 rounded flex items-center justify-center hover:bg-white/10 ${win.pinned ? 'text-[#C77D3F]' : 'text-gray-500'}`}
                  title={win.pinned ? 'Unpin (stop keeping on top)' : 'Pin on top'}
                >
                  <span className="material-symbols-outlined text-sm" style={win.pinned ? { fontVariationSettings: "'FILL' 1" } : undefined}>
                    keep
                  </span>
                </button>
                <button
                  onClick={() => minimizeWindow(win.id)}
                  className="w-9 h-9 md:h-6 md:w-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-100 hover:bg-white/10"
                  title="Minimize to taskbar"
                >
                  <span className="material-symbols-outlined text-base md:text-sm">remove</span>
                </button>
                {/* #185: maximize = leave the window and open the page's URL */}
                <button
                  onClick={() => maximizeToPage(win)}
                  className="w-9 h-9 md:h-6 md:w-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-100 hover:bg-white/10"
                  title="Maximize — open as full page"
                >
                  <span className="material-symbols-outlined text-base md:text-sm">crop_square</span>
                </button>
                <button
                  onClick={() => closeWindow(win.id)}
                  className="w-9 h-9 md:h-6 md:w-6 rounded flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-500/80"
                  title="Close"
                >
                  <span className="material-symbols-outlined text-base md:text-sm">close</span>
                </button>
              </div>
            </div>

            {/* Real route content — no iframe, no chrome (WindowedContext).
                Portal previews (#127) opt into an iframe so they get their own
                auth realm instead of inheriting the admin session. */}
            <div className="flex-1 min-h-0 bg-[#FAF6F0] overflow-hidden">
              {win.iframe
                ? <iframe src={win.route} title={win.title} className="w-full h-full border-0" />
                : <WindowContent route={win.route} />}
            </div>

            {/* Resize handles (pointer events — work on touch) */}
            {!win.maximized && (
              <>
                <div
                  className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize touch-none"
                  onPointerDown={e => onPointerDown(e, win, 'resize-se')}
                >
                  <div className="absolute bottom-1 right-1 w-3 h-3 border-r-2 border-b-2 border-gray-400/70 rounded-br" />
                </div>
                <div
                  className="absolute top-10 md:top-7 bottom-6 right-0 w-2 cursor-e-resize touch-none"
                  onPointerDown={e => onPointerDown(e, win, 'resize-e')}
                />
                <div
                  className="absolute bottom-0 left-2 right-6 h-2 cursor-s-resize touch-none"
                  onPointerDown={e => onPointerDown(e, win, 'resize-s')}
                />
              </>
            )}
          </div>
        )
      )}
    </div>
  );
};
